import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { parseConsolidatedContent, defaultContent } from './LandingPageContent.js';
import AuthNavItem from './AuthNavItem';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PublicIcon from '@mui/icons-material/Public';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleIcon from '@mui/icons-material/People';
import SpeedIcon from '@mui/icons-material/Speed';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StarIcon from '@mui/icons-material/Star';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CloseIcon from '@mui/icons-material/Close';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import PlagiarismOutlinedIcon from '@mui/icons-material/PlagiarismOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';

const MinimalProfileContainer = styled(Box)(() => ({
  padding: 'var(--spacing-xl) 0',
  borderBottom: '1px solid var(--color-action-disabled-background)',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: 'var(--color-action-hover)',
    transition: 'background-color 0.2s ease',
  },
}));

const CleanContactBlock = styled(Box)(() => ({
  padding: 'var(--spacing-xl)',
  borderLeft: '3px solid var(--color-primary-20)',
  backgroundColor: 'var(--color-background-paper)',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderLeftColor: 'var(--color-primary)',
    backgroundColor: 'var(--color-action-hover)',
  },
}));

const ViewCard = styled(Card)(() => ({
  height: '100%',
  backgroundColor: 'var(--color-background-paper)',
  border: '1px solid var(--color-action-disabled-background)',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 3px 8px var(--color-primary-12)',
    borderColor: 'var(--color-primary)',
    '& .MuiCardContent-root': {
      backgroundColor: 'var(--color-action-hover)',
    }
  },
}));

const HeroSection = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #005F94 0%, #004a78 50%, #003d63 100%)',
  color: 'var(--color-background-paper)',
  padding: 'clamp(4rem, 6vw, 6rem) 0',
  textAlign: 'center',
  position: 'relative',
  minHeight: '65vh',
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `url('/screenshot.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center top',
    backgroundAttachment: 'scroll',
    backgroundRepeat: 'no-repeat',
    filter: 'grayscale(100%) blur(0.3§px)',
    opacity: 0.08,
    transform: 'translateY(0px)',
    mask: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0.2) 80%, rgba(255,255,255,0.05) 100%)',
    WebkitMask: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0.2) 80%, rgba(255,255,255,0.05) 100%)',
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, transparent 0%, rgba(0, 95, 148, 0.3) 100%)',
    zIndex: 2,
  },
  '& > *': {
    position: 'relative',
    zIndex: 2,
  }
}));

const FeatureSection = styled(Container)(() => ({
  padding: 'clamp(2.5rem, 5vw, 4rem) 0',
}));

const ProfessionalButton = styled('button')(() => ({
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-background-paper)',
  border: '2px solid var(--color-primary)',
  padding: 'var(--spacing-md) var(--spacing-xl)',
  fontSize: 'var(--font-size-lg)',
  fontFamily: 'var(--font-family-heading)',
  fontWeight: 'var(--font-weight-semibold)',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  textTransform: 'none',
  lineHeight: 1.2,
  '&:hover': {
    backgroundColor: 'var(--color-secondary)',
    borderColor: 'var(--color-secondary)',
    transform: 'translateY(-1px)',
    boxShadow: '0 3px 8px var(--color-primary-20)',
  },
  '&:active': {
    transform: 'translateY(0)',
  }
}));

const SecondaryButton = styled('button')(() => ({
  backgroundColor: 'transparent',
  color: 'var(--color-primary)',
  border: '2px solid var(--color-primary)',
  padding: 'var(--spacing-md) var(--spacing-xl)',
  fontSize: 'var(--font-size-lg)',
  fontFamily: 'var(--font-family-heading)',
  fontWeight: 'var(--font-weight-semibold)',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  textTransform: 'none',
  lineHeight: 1.2,
  '&:hover': {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-background-paper)',
    transform: 'translateY(-1px)',
    boxShadow: '0 3px 8px var(--color-primary-20)',
  },
  '&:active': {
    transform: 'translateY(0)',
  }
}));

const SectionDivider = styled(Box)(() => ({
  height: '1px',
  background: 'var(--color-action-disabled-background)',
  margin: 'var(--spacing-xl) auto',
  maxWidth: '200px',
  opacity: 0.8
}));

const CompactSectionDivider = styled(Box)(() => ({
  height: '2px',
  background: 'linear-gradient(90deg, transparent 0%, var(--color-primary) 20%, var(--color-primary) 80%, transparent 100%)',
  margin: 'var(--spacing-lg) auto var(--spacing-md) auto',
  maxWidth: '300px',
  opacity: 0.6
}));

const ProminentSectionBreak = styled(Box)(() => ({
  background: 'linear-gradient(180deg, var(--color-background) 0%, var(--color-action-selected) 50%, var(--color-background) 100%)',
  padding: 'var(--spacing-md) 0',
  margin: 'var(--spacing-lg) 0 var(--spacing-sm) 0',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '400px',
    height: '3px',
    background: 'linear-gradient(90deg, transparent 0%, var(--color-primary) 15%, var(--color-secondary) 85%, transparent 100%)',
    borderRadius: '2px',
    opacity: 0.4,
  }
}));

const IconWrapper = styled(Box)(() => ({
  color: 'var(--color-primary)', // Primary blue for "What We Offer" icons
  marginBottom: 'var(--spacing-md)',
  textAlign: 'center',
  opacity: 0.9,
  '& svg': {
    fontSize: '2.5rem'
  }
}));

const ServiceCard = styled(Card)(() => ({
  height: '100%',
  backgroundColor: 'var(--color-background-paper)',
  border: '1px solid var(--color-action-disabled-background)',
  borderRadius: '16px',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 2px 8px var(--color-primary-06)',
  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)',
  backdropFilter: 'blur(10px)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, var(--color-action-selected) 0%, transparent 50%, var(--color-action-selected) 100%)',
    opacity: 0,
    transition: 'opacity 0.4s ease',
    zIndex: 1,
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 20px 40px var(--color-primary-15)',
          borderColor: 'var(--color-action-disabled)',
    '&::before': {
      opacity: 1,
    },
    '& .service-icon': {
      transform: 'scale(1.1) rotate(5deg)',
      background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
    },
    '& .service-content': {
      transform: 'translateY(-2px)',
    }
  },
}));

const ServiceIconWrapper = styled(Box)(() => ({
  width: '80px',
  height: '80px',
  borderRadius: '20px',
  background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-80) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 'var(--spacing-lg)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: '-2px',
    borderRadius: '22px',
    background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary), var(--color-primary))',
    zIndex: -1,
    opacity: 0,
    transition: 'opacity 0.4s ease',
  },
  '&:hover::after': {
    opacity: 1,
  },
  '& svg': {
    fontSize: '2.2rem',
    color: 'var(--color-background-paper)',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
  }
}));

// Alternative Design 2: Geometric/Modern Cards
const GeometricServiceCard = styled(Card)(() => ({
  height: '100%',
  backgroundColor: 'var(--color-background-paper)',
  border: 'none',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, transparent 0%, transparent 48%, var(--color-primary) 50%, transparent 52%, transparent 100%)',
    opacity: 0.1,
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '60px',
    height: '60px',
    background: 'var(--color-primary)',
    clipPath: 'polygon(100% 0%, 0% 0%, 100% 100%)',
    opacity: 0.8,
    zIndex: 1,
  },
  '&:hover': {
    transform: 'translateX(4px)',
    boxShadow: '-8px 8px 24px var(--color-primary-15)',
    '&::before': {
      opacity: 0.2,
    },
    '&::after': {
      width: '80px',
      height: '80px',
    }
  },
}));

const GeometricIconWrapper = styled(Box)(() => ({
  width: '64px',
  height: '64px',
  backgroundColor: 'transparent',
  border: '3px solid var(--color-primary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 'var(--spacing-lg)',
  position: 'relative',
  zIndex: 2,
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-3px',
    background: 'var(--color-primary)',
    zIndex: -1,
    transform: 'scale(0)',
    transition: 'transform 0.3s ease',
  },
  '&:hover': {
    '&::before': {
      transform: 'scale(1)',
    },
    '& svg': {
      color: 'var(--color-background-paper)',
    }
  },
  '& svg': {
    fontSize: '1.8rem',
    color: 'var(--color-primary)',
    transition: 'color 0.3s ease',
  }
}));

// Clean Flat Service Components
const CleanServiceContainer = styled(Box)(() => ({
  height: '100%',
  backgroundColor: 'var(--color-background-paper)',
  border: '1px solid var(--color-action-disabled-background)',
  transition: 'all 0.2s ease',
  position: 'relative',
  '&:hover': {
    borderColor: 'var(--color-primary)',
    backgroundColor: 'var(--color-action-hover)',
    '& .service-badge': {
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-background-paper)',
    }
  },
}));

const CleanServiceBadge = styled(Box)(() => ({
  position: 'absolute',
  top: 'var(--spacing-lg)',
  right: 'var(--spacing-lg)',
  backgroundColor: 'var(--color-primary-10)',
  color: 'var(--color-primary)',
  padding: '4px 12px',
  fontSize: 'var(--font-size-xs)',
  fontFamily: 'var(--font-family-base)',
  fontWeight: 'var(--font-weight-semibold)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  transition: 'all 0.2s ease',
  zIndex: 2,
}));

const CleanServiceFeatureList = styled(Box)(() => ({
  marginTop: 'var(--spacing-md)',
  '& .feature-item': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 'var(--spacing-xs)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    '&:last-child': {
      marginBottom: 0
    },
    '& .feature-icon': {
      marginRight: 'var(--spacing-xs)',
      fontSize: '1rem',
      color: 'var(--color-secondary)',
    }
  }
}));

const CleanServiceIconWrapper = styled(Box)(() => ({
  width: '48px',
  height: '48px',
  backgroundColor: 'var(--color-primary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 'var(--spacing-lg)',
  transition: 'all 0.2s ease',
  position: 'relative',
  '& svg': {
    fontSize: '1.5rem',
    color: 'var(--color-background-paper)',
  }
}));



// Horizontal Pipeline Components  
const PipelineContainer = styled(Box)(() => ({
  maxWidth: '1400px',
  margin: '0 auto',
  padding: 'var(--spacing-xl) 0',
  position: 'relative',
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-lg) var(--spacing-sm)',
  }
}));

const PipelineFlow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: 'var(--spacing-md)',
  '@media (max-width: 1024px)': {
    flexDirection: 'column',
    gap: 'var(--spacing-lg)',
  },
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
    padding: '0 var(--spacing-sm)',
  }
}));

const PipelineStage = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})(({ isLast }) => ({
  flex: 1,
  backgroundColor: 'var(--color-background-paper)',
  border: '1px solid var(--color-action-disabled-background)',
  padding: 'var(--spacing-lg)',
  position: 'relative',
  transition: 'all 0.2s ease',
  borderRadius: '8px',
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-md)',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 95, 148, 0.08)',
  },
  '&:hover': {
    borderColor: 'var(--color-primary)',
    backgroundColor: 'var(--color-action-hover)',
    '& .pipeline-icon': {
      backgroundColor: 'var(--color-secondary)',
      transform: 'scale(1.05)',
    }
  },
  '&::after': isLast ? {} : {
    content: '"→"',
    position: 'absolute',
    right: '-22px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 'var(--font-size-xl)',
    color: 'var(--color-primary)',
    backgroundColor: 'var(--color-background)',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--color-action-disabled-background)',
    borderRadius: '50%',
    zIndex: 2,
    '@media (max-width: 1024px)': {
      display: 'none',
    }
  }
}));

const PipelineTitle = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-heading) !important',
  fontWeight: 'var(--font-weight-bold) !important',
  fontSize: 'var(--font-size-lg) !important',
  color: 'var(--color-primary) !important',
  marginBottom: 'var(--spacing-sm) !important',
  lineHeight: 'var(--line-height-tight) !important',
  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-md) !important',
    marginBottom: 'var(--spacing-xs) !important',
  }
}));

const PipelineDescription = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-base) !important',
  fontSize: 'var(--font-size-sm) !important',
  color: 'var(--color-text-secondary) !important',
  lineHeight: 'var(--line-height-relaxed) !important',
  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-xs) !important',
    lineHeight: 'var(--line-height-normal) !important',
  }
}));

const PipelineIconContainer = styled(Box)(() => ({
  width: '60px',
  height: '60px',
  backgroundColor: 'var(--color-primary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 'var(--spacing-md)',
  transition: 'all 0.2s ease',
  borderRadius: '8px',
  '@media (max-width: 768px)': {
    width: '48px',
    height: '48px',
    marginBottom: 'var(--spacing-sm)',
    borderRadius: '6px',
  }
}));

// Modern Tech-Inspired Components (for custom solutions)
const TechGridContainer = styled(Box)(() => ({
  maxWidth: '1400px',
  margin: '0 auto',
  padding: 'var(--spacing-xl) 0',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `radial-gradient(circle at 1px 1px, var(--color-primary) 1px, transparent 0)`,
    backgroundSize: '20px 20px',
    opacity: 0.03,
    pointerEvents: 'none',
  }
}));

const TechGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gap: 'var(--spacing-xl)',
  maxWidth: '1800px',
  margin: '0 auto',
  // First row: 3 cards spanning 2 columns each (positions 1-2, 3-4, 5-6)
  '& > *:nth-of-type(1)': {
    gridColumn: '1 / 3',
  },
  '& > *:nth-of-type(2)': {
    gridColumn: '3 / 5',
  },
  '& > *:nth-of-type(3)': {
    gridColumn: '5 / 7',
  },
  // Second row: 2 cards centered, each spanning 2 columns (positions 2-3, 4-5)
  '& > *:nth-of-type(4)': {
    gridColumn: '2 / 4',
  },
  '& > *:nth-of-type(5)': {
    gridColumn: '4 / 6',
  },
  '@media (max-width: 1200px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    '& > *:nth-of-type(1)': {
      gridColumn: '1 / 2',
    },
    '& > *:nth-of-type(2)': {
      gridColumn: '2 / 3',
    },
    '& > *:nth-of-type(3)': {
      gridColumn: '1 / 2',
    },
    '& > *:nth-of-type(4)': {
      gridColumn: '2 / 3',
    },
    '& > *:nth-of-type(5)': {
      gridColumn: '1 / 3',
    },
  },
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    '& > *:nth-of-type(n)': {
      gridColumn: '1 / 2',
    },
    gap: 'var(--spacing-lg)',
  }
}));

const TechCard = styled(Box)(() => ({
  backgroundColor: 'var(--color-background-paper)',
  border: '1px solid var(--color-action-disabled-background)',
  padding: 'var(--spacing-xl)',
  position: 'relative',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, var(--color-primary) 0%, var(--color-secondary) 50%, transparent 100%)',
    opacity: 0.6,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '2px',
    height: '40px',
    background: 'linear-gradient(180deg, var(--color-primary) 0%, transparent 100%)',
    opacity: 0.4,
  },
  '&:hover': {
    borderColor: 'var(--color-primary)',
    backgroundColor: 'var(--color-action-hover)',
    transform: 'translateY(-4px)',
    '&::before': {
      opacity: 1,
    },
    '&::after': {
      opacity: 0.8,
    },
    '& .tech-badge': {
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-background-paper)',
    }
  }
}));

const TechBadge = styled(Box)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  backgroundColor: 'transparent',
  border: '2px solid var(--color-primary)',
  color: 'var(--color-primary)',
  fontFamily: 'var(--font-family-heading)',
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-md)',
  marginBottom: 'var(--spacing-lg)',
  transition: 'all 0.3s ease',
}));

const TechTitle = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-heading) !important',
  fontWeight: 'var(--font-weight-bold) !important',
  fontSize: 'var(--font-size-xl) !important',
  color: 'var(--color-text-primary) !important',
  marginBottom: 'var(--spacing-md) !important',
  lineHeight: 'var(--line-height-tight) !important',
  background: 'linear-gradient(135deg, var(--color-text-primary) 0%, var(--color-primary) 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const TechDescription = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-base) !important',
  fontSize: 'var(--font-size-md) !important',
  color: 'var(--color-text-secondary) !important',
  lineHeight: 'var(--line-height-relaxed) !important',
  marginBottom: 'var(--spacing-md) !important',
}));

const TechFeatureList = styled(Box)(() => ({
  '& .tech-feature': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--spacing-xs)',
    marginBottom: 'var(--spacing-xs)',
    '&:last-child': {
      marginBottom: 0
    },
    '&::before': {
      content: '"▸"',
      color: 'var(--color-primary)',
      fontFamily: 'var(--font-family-heading)',
      fontWeight: 'var(--font-weight-bold)',
      fontSize: 'var(--font-size-sm)',
      marginTop: '2px',
      flexShrink: 0,
    }
  }
}));

// Progressive Step Flow Components (for workflow sections)
const StepFlowContainer = styled(Box)(() => ({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'var(--spacing-xl) 0',
  position: 'relative',
}));

const StepItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})(({ isLast }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--spacing-xl)',
  position: 'relative',
  marginBottom: isLast ? 0 : 'var(--spacing-xxl)',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 'var(--spacing-lg)',
    marginBottom: isLast ? 0 : 'var(--spacing-xl)',
  }
}));

const StepCircle = styled(Box)(() => ({
  width: '60px',
  height: '60px',
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-background-paper)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-family-heading)',
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-xl)',
  flexShrink: 0,
  position: 'relative',
  zIndex: 2,
  '@media (max-width: 768px)': {
    width: '50px',
    height: '50px',
    fontSize: 'var(--font-size-lg)',
  }
}));

const StepConnector = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})(({ isLast }) => ({
  position: 'absolute',
  left: '30px',
  top: '60px',
  width: '2px',
  height: isLast ? 0 : 'calc(100% + var(--spacing-lg))',
  backgroundColor: 'var(--color-action-disabled-background)',
  zIndex: 1,
  '@media (max-width: 768px)': {
    display: 'none',
  }
}));

const StepContent = styled(Box)(() => ({
  flex: 1,
  backgroundColor: 'var(--color-background-paper)',
  border: '1px solid var(--color-action-disabled-background)',
  padding: 'var(--spacing-xl)',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'var(--color-primary)',
    backgroundColor: 'var(--color-action-hover)',
  },
  '@media (max-width: 768px)': {
    width: '100%',
  }
}));

const StepTitle = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-heading) !important',
  fontWeight: 'var(--font-weight-bold) !important',
  fontSize: 'var(--font-size-xl) !important',
  color: 'var(--color-text-primary) !important',
  marginBottom: 'var(--spacing-md) !important',
  lineHeight: 'var(--line-height-tight) !important',
}));

const StepDescription = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-base) !important',
  fontSize: 'var(--font-size-md) !important',
  color: 'var(--color-text-secondary) !important',
  lineHeight: 'var(--line-height-relaxed) !important',
}));

// Clean Analysis Level Components
const CleanAnalysisContainer = styled(Box)(() => ({
  maxWidth: '1000px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
}));

const CleanAnalysisItem = styled(Box)(() => ({
  backgroundColor: 'var(--color-background-paper)',
  border: '1px solid var(--color-action-disabled-background)',
  padding: 'var(--spacing-lg)',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'var(--color-primary)',
    backgroundColor: 'var(--color-action-hover)',
  },
}));

const CleanAnalysisHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-md)',
  marginBottom: 'var(--spacing-sm)',
}));

const CleanAnalysisNumber = styled(Box)(() => ({
  width: '32px',
  height: '32px',
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-background-paper)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-family-heading)',
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-md)',
  flexShrink: 0,
}));

const CleanAnalysisTitle = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-heading) !important',
  fontWeight: 'var(--font-weight-bold) !important',
  fontSize: 'var(--font-size-lg) !important',
  color: 'var(--color-primary) !important',
  lineHeight: 'var(--line-height-tight) !important',
}));

const CleanAnalysisDescription = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-base) !important',
  fontSize: 'var(--font-size-md) !important',
  color: 'var(--color-text-secondary) !important',
  lineHeight: 'var(--line-height-relaxed) !important',
}));

const ServiceListItem = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: 'var(--spacing-sm)',
  '&:last-child': {
    marginBottom: 0
  }
}));

const ServiceBullet = styled(Box)(() => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: '#E2001A', // Red accent for bullet points
  marginTop: '8px',
  marginRight: 'var(--spacing-md)',
  flexShrink: 0
}));

const ContentSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'alternate',
})(({ alternate }) => ({
  backgroundColor: alternate ? 'var(--color-action-hover)' : 'var(--color-background)',
  padding: 'clamp(2.5rem, 5vw, 4rem) 0',
}));

// Clean Minimal Pricing Components
const CleanPricingContainer = styled(Box)(() => ({
  maxWidth: '1000px',
  margin: '0 auto',
  position: 'relative',
}));

const CleanPricingTable = styled(Box)(() => ({
  background: 'var(--color-background-paper)',
  border: '1px solid var(--color-action-disabled-background)',
  position: 'relative',
}));

const CleanPricingHeader = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '1.5fr repeat(4, 1fr)',
  borderBottom: '2px solid var(--color-action-disabled-background)',
  backgroundColor: 'var(--color-action-hover)',
  '@media (max-width: 1024px)': {
    display: 'none',
  }
}));

const CleanHeaderCell = styled(Box)(() => ({
  padding: 'var(--spacing-lg)',
  fontFamily: 'var(--font-family-heading)',
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-lg)',
  color: 'var(--color-text-primary)',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRight: '1px solid var(--color-action-disabled-background)',
  '&:first-of-type': {
    textAlign: 'left',
    alignItems: 'flex-start',
  },
  '&:last-of-type': {
    borderRight: 'none',
  }
}));

const CleanPlanBadge = styled(Box)(() => ({
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-background-paper)',
  fontSize: 'var(--font-size-xs)',
  fontFamily: 'var(--font-family-base)',
  fontWeight: 'var(--font-weight-semibold)',
  padding: '4px 12px',
  marginTop: 'var(--spacing-xs)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
}));

const CleanPricingBody = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '1.5fr repeat(4, 1fr)',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: '1fr',
    gap: 'var(--spacing-lg)',
    padding: 'var(--spacing-lg)',
  }
}));

const CleanRowLabel = styled(Box)(() => ({
  padding: 'var(--spacing-lg)',
  borderBottom: '1px solid var(--color-action-disabled-background)',
  borderRight: '1px solid var(--color-action-disabled-background)',
  fontFamily: 'var(--font-family-base)',
  fontWeight: 'var(--font-weight-medium)',
  fontSize: 'var(--font-size-md)',
  color: 'var(--color-text-primary)',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'var(--color-background-paper)',
  '@media (max-width: 1024px)': {
    display: 'none',
  }
}));

const CleanPriceCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isPopular',
})(({ isPopular }) => ({
  padding: 'var(--spacing-lg)',
  borderBottom: '1px solid var(--color-action-disabled-background)',
  borderRight: '1px solid var(--color-action-disabled-background)',
  textAlign: 'center',
  fontFamily: 'var(--font-family-heading)',
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-lg)',
  color: 'var(--color-text-primary)',
  backgroundColor: isPopular ? 'var(--color-action-hover)' : 'var(--color-background-paper)',
  position: 'relative',
  transition: 'all 0.2s ease',
  '&:last-of-type': {
    borderRight: 'none',
  },
  '&:hover': {
    backgroundColor: 'var(--color-action-selected)',
  },
  '@media (max-width: 1024px)': {
    padding: 'var(--spacing-xl)',
    border: isPopular ? '2px solid var(--color-primary)' : '1px solid var(--color-action-disabled-background)',
    backgroundColor: isPopular ? 'var(--color-action-hover)' : 'var(--color-background-paper)',
    borderBottom: 'none',
    borderRight: 'none',
  }
}));

const MobilePlanHeader = styled(Box)(() => ({
  display: 'none',
  '@media (max-width: 1024px)': {
    display: 'block',
    textAlign: 'center',
    marginBottom: 'var(--spacing-lg)',
  }
}));

const MobilePlanTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isPopular',
})(({ isPopular }) => ({
  fontFamily: 'var(--font-family-heading) !important',
  fontWeight: 'var(--font-weight-bold) !important',
  fontSize: 'var(--font-size-xl) !important',
  color: isPopular ? 'var(--color-primary) !important' : 'var(--color-text-primary) !important',
  marginBottom: 'var(--spacing-xs) !important',
}));

const PriceDisplay = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
  marginBottom: 'var(--spacing-md)',
}));

const PriceAmount = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-heading) !important',
  fontWeight: 'var(--font-weight-bold) !important',
  fontSize: 'var(--font-size-xl) !important',
  color: 'var(--color-text-primary) !important',
  lineHeight: 'var(--line-height-tight) !important',
}));

const PriceLabel = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-base) !important',
  fontSize: 'var(--font-size-sm) !important',
  color: 'var(--color-text-secondary) !important',
  fontWeight: 'var(--font-weight-medium) !important',
}));

const PricingSeparator = styled(Box)(() => ({
  height: '1px',
  backgroundColor: 'rgba(0, 95, 148, 0.1)',
  margin: 'var(--spacing-sm) 0',
  '@media (max-width: 1024px)': {
    margin: 'var(--spacing-md) 0',
  }
}));

const CleanYearlyPriceCell = styled(CleanPriceCell)(() => ({
  '@media (max-width: 1024px)': {
    display: 'none !important',
  }
}));

const CleanFeatureGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 'var(--spacing-sm)',
  padding: 'var(--spacing-lg)',
  backgroundColor: 'var(--color-background-paper)',
  border: '1px solid var(--color-action-disabled-background)',
  marginBottom: 'var(--spacing-md)',
}));

const CleanFeatureItem = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  padding: 'var(--spacing-xs) 0',
}));

const OverlayContainer = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none', // Allow clicks to pass through to the iframe
  '@media (max-width: 768px)': {
    display: 'none', // Hide overlay cards on mobile devices
  },
}));

const OverlayBox = styled(Box)(() => ({
  position: 'absolute',
  pointerEvents: 'auto',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  border: '1px solid rgba(0, 95, 148, 0.15)',
  borderRadius: '16px',
  padding: 'var(--spacing-lg)',
  boxShadow: '0 20px 40px rgba(0, 95, 148, 0.08), 0 8px 16px rgba(0, 95, 148, 0.04)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'var(--spacing-sm)',
  backdropFilter: 'blur(20px) saturate(180%)',
  width: '240px',
  textAlign: 'left',
  willChange: 'transform, opacity, pointer-events',
  transition: 'all 0.2s ease-out',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 25px 50px rgba(0, 95, 148, 0.12), 0 12px 24px rgba(0, 95, 148, 0.08)',
    borderColor: 'rgba(0, 95, 148, 0.25)',
    backgroundColor: 'rgba(255, 255, 255, 0.99)',
  },
}));

const OverlayHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
  width: '100%',
}));

const OverlayNumber = styled(Box)(() => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, var(--color-primary) 0%, rgba(0, 95, 148, 0.8) 100%)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.9rem',
  fontWeight: 'var(--font-weight-bold)',
  flexShrink: 0,
  boxShadow: '0 4px 12px rgba(0, 95, 148, 0.3)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: '0 6px 16px rgba(0, 95, 148, 0.4)',
  },
}));

const ModernNote = styled(Box)(() => ({
  padding: 'var(--spacing-lg)',
  backgroundColor: 'rgba(0, 95, 148, 0.05)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 95, 148, 0.15)',
  marginBottom: 'var(--spacing-lg)',
  textAlign: 'center',
}));

// Top Branding Bar Components
const TopBrandingBar = styled(Box)(() => ({
  backgroundColor: '#005F94', // Force blue color for landing page
  padding: 'var(--spacing-md) 0',
  position: 'relative',
  zIndex: 10,
}));

const BrandingContainer = styled(Box)(() => ({
  maxWidth: '1320px',
  margin: '0 auto',
  padding: '0 var(--spacing-lg)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--spacing-md)',
}));

const BrandingLogo = styled('img')(() => ({
  filter: 'brightness(0) invert(1)',
  width: '32px',
  height: '32px',
  flexShrink: 0,
}));

const BrandingText = styled(Typography)(() => ({
  fontFamily: 'var(--font-family-heading) !important',
  fontWeight: 'var(--font-weight-bold) !important',
  fontSize: 'var(--font-size-xl) !important',
  color: 'var(--color-background-paper) !important',
  letterSpacing: '0.02em !important',
  lineHeight: 'var(--line-height-tight) !important',
}));

const BrandingLeft = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-md)',
}));



const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dashboardRef = useRef(null);
  const overlayBoxesRef = useRef([]);
  const gsapTimelineRef = useRef(null);
  const iframeRef = useRef(null);
  const [selectedPreview, setSelectedPreview] = useState('myanmar');
  const [frameworkModalOpen, setFrameworkModalOpen] = useState(false);
  const [visibleWorkflowCards, setVisibleWorkflowCards] = useState([]);
  const workflowRefs = useRef([]);
  const [workflowTriggered, setWorkflowTriggered] = useState(false);

  const [visibleAnalysisCards, setVisibleAnalysisCards] = useState([]);
  const analysisRefs = useRef([]);
  const [analysisTriggered, setAnalysisTriggered] = useState(false);

  // Parallax effect for hero section
  const [scrollY, setScrollY] = useState(0);
  const parallaxRef = useRef(false);
  const [keyWordsAnimated, setKeyWordsAnimated] = useState(false);

  // Content management state - now using consolidated content structure
  const [contentLoading, setContentLoading] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle window resize to hide/show cards based on screen size
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile && overlayBoxesRef.current) {
        // Hide cards immediately on mobile
        overlayBoxesRef.current.forEach(box => {
          if (box) {
            box.style.display = 'none';
          }
        });
      } else if (!isMobile && overlayBoxesRef.current) {
        // Show cards on desktop (but let animations control visibility)
        overlayBoxesRef.current.forEach(box => {
          if (box) {
            box.style.display = 'flex';
          }
        });
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once on mount
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Improved GSAP ScrollTrigger animation for dashboard overlay boxes
  useEffect(() => {
    if (!dashboardRef.current || !window.gsap) return;
    
    // Skip animations on mobile devices
    if (window.innerWidth <= 768) return;

    // Register ScrollTrigger plugin
    window.gsap.registerPlugin(window.ScrollTrigger);

    let animationState = 'hidden'; // Track animation state to prevent conflicts
    let hideTimer = null;
    let userInteracted = false; // Track if user has interacted with the dashboard

    // Set initial state for all boxes (simplified transforms)
    window.gsap.set(overlayBoxesRef.current, {
      opacity: 0,
      y: 30,
      scale: 0.95,
      pointerEvents: 'none'
    });

    // Create timeline for staggered animation
    const tl = window.gsap.timeline({
      paused: true,
      onStart: () => {
        animationState = 'animating';
      },
      onComplete: () => {
        animationState = 'visible';
        // Auto-hide after 10 seconds with smoother transition
        hideTimer = setTimeout(() => {
          if (animationState === 'visible') {
            animationState = 'hiding';
            window.gsap.to(overlayBoxesRef.current, {
              opacity: 0,
              y: -15,
              scale: 0.98,
              pointerEvents: 'none',
              duration: 1.0,
              ease: "power2.inOut",
              stagger: 0.08,
              onComplete: () => {
                animationState = 'hidden';
              }
            });
          }
        }, 10000);
      }
    });

    // Add staggered animations to timeline (smoother, less aggressive)
    tl.to(overlayBoxesRef.current, {
      opacity: 1,
      y: 0,
      scale: 1,
      pointerEvents: 'auto',
      duration: 0.8,
      ease: "power2.out",
      stagger: 0.4
    });

    gsapTimelineRef.current = tl;

    // Create ScrollTrigger with debouncing
    let scrollTriggerTimeout = null;
    
    const scrollTrigger = window.ScrollTrigger.create({
      trigger: dashboardRef.current,
      start: "top 60%",
      end: "bottom 40%",
      onEnter: () => {
        // Clear any existing timeout to debounce
        if (scrollTriggerTimeout) clearTimeout(scrollTriggerTimeout);
        
        scrollTriggerTimeout = setTimeout(() => {
          if (animationState === 'hidden') {
            userInteracted = false; // Reset interaction flag when scrolling back into view
            if (hideTimer) clearTimeout(hideTimer);
            tl.restart();
          }
        }, 100); // 100ms debounce
      },
      onLeaveBack: () => {
        if (scrollTriggerTimeout) clearTimeout(scrollTriggerTimeout);
        
        scrollTriggerTimeout = setTimeout(() => {
          if (animationState !== 'hidden') {
            animationState = 'hiding';
            if (hideTimer) clearTimeout(hideTimer);
            tl.pause();
            window.gsap.to(overlayBoxesRef.current, {
              opacity: 0,
              y: 30,
              scale: 0.95,
              pointerEvents: 'none',
              duration: 0.6,
              ease: "power2.inOut",
              stagger: 0.05,
              onComplete: () => {
                animationState = 'hidden';
              }
            });
          }
        }, 100); // 100ms debounce
      }
    });

    // Hide completely on iframe interaction
    const handleInteraction = () => {
      if (animationState === 'visible' || animationState === 'animating') {
        userInteracted = true; // Mark that user has interacted
        animationState = 'hidden';
        if (hideTimer) clearTimeout(hideTimer);
        window.gsap.to(overlayBoxesRef.current, {
          opacity: 0,
          y: -20,
          scale: 0.95,
          pointerEvents: 'none',
          duration: 0.3,
          ease: "power2.inOut",
          stagger: 0.03
        });
      }
    };

    // Also hide on any click within the iframe area
    const handleClick = () => {
      if (animationState === 'visible' || animationState === 'animating') {
        userInteracted = true; // Mark that user has interacted
        animationState = 'hidden';
        if (hideTimer) clearTimeout(hideTimer);
        window.gsap.to(overlayBoxesRef.current, {
          opacity: 0,
          y: -20,
          scale: 0.95,
          pointerEvents: 'none',
          duration: 0.3,
          ease: "power2.inOut",
          stagger: 0.03
        });
      }
    };

    const iframe = iframeRef.current;
    const dashboardContainer = dashboardRef.current;
    
    if (iframe) {
      iframe.addEventListener('mouseenter', handleInteraction);
    }
    
    if (dashboardContainer) {
      dashboardContainer.addEventListener('click', handleClick);
      dashboardContainer.addEventListener('mousedown', handleClick);
    }

    return () => {
      if (scrollTriggerTimeout) clearTimeout(scrollTriggerTimeout);
      if (hideTimer) clearTimeout(hideTimer);
      window.ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      if (iframe) {
        iframe.removeEventListener('mouseenter', handleInteraction);
      }
      if (dashboardContainer) {
        dashboardContainer.removeEventListener('click', handleClick);
        dashboardContainer.removeEventListener('mousedown', handleClick);
      }
    };
  }, []);

  // Trigger key words animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setKeyWordsAnimated(true);
    }, 500); // Start animation 500ms after page load
    return () => clearTimeout(timer);
  }, []);

  // Fetch landing page content from database
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setContentLoading(true);
        const response = await fetch('/api/content/sections/landing_page');
        if (response.ok) {
          const data = await response.json();
          if (data.sections) {
            // Parse consolidated content structure
            const sectionsArray = Object.values(data.sections).map(section => ({
              section_key: Object.keys(data.sections).find(key => data.sections[key] === section),
              content: section.content
            }));
            const parsedContent = parseConsolidatedContent(sectionsArray);
            setContent(prev => ({ ...prev, ...parsedContent }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch landing page content:', error);
        // Keep default content if fetch fails
      } finally {
        setContentLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Move these arrays to the top to avoid ReferenceError
  const workflowSteps = [
    {
      title: content.workflow_step_1_title,
      description: content.workflow_step_1_description,
      icon: DesignServicesIcon,
    },
    {
      title: content.workflow_step_2_title,
      description: content.workflow_step_2_description,
      icon: StorageIcon,
    },
    {
      title: content.workflow_step_3_title,
      description: content.workflow_step_3_description,
      icon: TuneIcon,
    },
    {
      title: content.workflow_step_4_title,
      description: content.workflow_step_4_description,
      icon: AnalyticsIcon,
    },
    {
      title: content.workflow_step_5_title,
      description: content.workflow_step_5_description,
      icon: DashboardIcon,
    },
  ];

  const customSolutionSteps = [
    {
      title: content.solution_step_1_title,
      description: content.solution_step_1_description,
      details: content.solution_step_1_details ? content.solution_step_1_details.split('|') : []
    },
    {
      title: content.solution_step_2_title,
      description: content.solution_step_2_description,
      details: content.solution_step_2_details ? content.solution_step_2_details.split('|') : []
    },
    {
      title: content.solution_step_3_title,
      description: content.solution_step_3_description,
      details: content.solution_step_3_details ? content.solution_step_3_details.split('|') : []
    },
    {
      title: content.solution_step_4_title,
      description: content.solution_step_4_description,
      details: content.solution_step_4_details ? content.solution_step_4_details.split('|') : []
    },
    {
      title: content.solution_step_5_title,
      description: content.solution_step_5_description,
      details: content.solution_step_5_details ? content.solution_step_5_details.split('|') : []
    },
  ];

  const analysisLevels = [
    {
      level: content.analysis_level_1_title,
      description: content.analysis_level_1_description,
    },
    {
      level: content.analysis_level_2_title,
      description: content.analysis_level_2_description,
    },
    {
      level: content.analysis_level_3_title,
      description: content.analysis_level_3_description,
    },
    {
      level: content.analysis_level_4_title,
      description: content.analysis_level_4_description,
    },
    {
      level: content.analysis_level_5_title,
      description: content.analysis_level_5_description,
    },
  ];

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };


  // Simplified animation for workflow cards - trigger once when in view
  useEffect(() => {
    if (!workflowRefs.current) return;
    const section = workflowRefs.current[0]?.parentElement?.parentElement?.parentElement;
    if (!section) return;
    let observer;
    observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !workflowTriggered) {
          setWorkflowTriggered(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(section);
    return () => observer && observer.disconnect();
  }, [workflowTriggered]);

  useEffect(() => {
    if (workflowTriggered && visibleWorkflowCards.length < workflowSteps.length) {
      let i = visibleWorkflowCards.length;
      const timeout = setTimeout(() => {
        setVisibleWorkflowCards((prev) =>
          prev.includes(i) ? prev : [...prev, i]
        );
      }, 80 * i); // Slightly slower for smoothness
      return () => clearTimeout(timeout);
    }
  }, [workflowTriggered, visibleWorkflowCards, workflowSteps.length]);

  // Simplified animation for analysis cards - trigger once when in view
  useEffect(() => {
    if (!analysisRefs.current) return;
    const section = analysisRefs.current[0]?.parentElement?.parentElement?.parentElement;
    if (!section) return;
    let observer;
    observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !analysisTriggered) {
          setAnalysisTriggered(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(section);
    return () => observer && observer.disconnect();
  }, [analysisTriggered]);

  useEffect(() => {
    if (analysisTriggered && visibleAnalysisCards.length < analysisLevels.length) {
      let i = visibleAnalysisCards.length;
      const timeout = setTimeout(() => {
        setVisibleAnalysisCards((prev) =>
          prev.includes(i) ? prev : [...prev, i]
        );
      }, 80 * i); // Slightly slower for smoothness
      return () => clearTimeout(timeout);
    }
  }, [analysisTriggered, visibleAnalysisCards, analysisLevels.length]);

  const handleViewSelect = (viewCode) => {
    navigate(`/${viewCode.toLowerCase()}`);
  };

  const pricingPlans = [
    ...(content.pricing_plan_1 ? [content.pricing_plan_1.split('|')].map(([plan, monthly, yearly]) => ({ plan, monthly, yearly })) : []),
    ...(content.pricing_plan_2 ? [content.pricing_plan_2.split('|')].map(([plan, monthly, yearly]) => ({ plan, monthly, yearly })) : []),
    ...(content.pricing_plan_3 ? [content.pricing_plan_3.split('|')].map(([plan, monthly, yearly]) => ({ plan, monthly, yearly })) : []),
    ...(content.pricing_plan_4 ? [content.pricing_plan_4.split('|')].map(([plan, monthly, yearly]) => ({ plan, monthly, yearly })) : []),
  ];

  const includedFeatures = content.included_features ? content.included_features.split('|') : [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'var(--color-background)' }}>
      {/* Top Branding Bar */}
      <TopBrandingBar>
        <BrandingContainer>
          <BrandingLeft>
            <BrandingLogo src="/favico.svg" alt="Situation Room Logo" />
            <BrandingText variant="h4">situation-room.ai</BrandingText>
          </BrandingLeft>
          <AuthNavItem />
        </BrandingContainer>
      </TopBrandingBar>

      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
          <Typography 
            variant="h1" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontFamily: 'var(--font-family-heading)',
              fontWeight: 'var(--font-weight-bold)', 
              fontSize: 'clamp(2rem, 4vw, 3.5rem)',
              lineHeight: 'var(--line-height-tight)',
              mb: 'var(--spacing-md)',
              color: 'var(--color-background-paper)',
              letterSpacing: 'var(--letter-spacing-tight)',
              // Parallax effect - moves slower than scroll, fades out
              transform: `translate3d(0, ${scrollY * 0.3}px, 0)`,
              opacity: Math.max(0, 1 - scrollY / 400),
              transition: 'none', // Disable transitions for smooth parallax
              willChange: 'transform, opacity', // Optimize for animations
            }}
          >
            {content.hero_title}
          </Typography>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
              lineHeight: 'var(--line-height-normal)',
              mb: 'var(--spacing-lg)', 
              color: 'var(--color-background-paper)',
              maxWidth: '800px',
              mx: 'auto',
              fontWeight: 'var(--font-weight-normal)',
              // Parallax effect - moves slightly faster than title, different fade rate
              transform: `translate3d(0, ${scrollY * 0.5}px, 0)`,
              opacity: Math.max(0, (0.95) * (1 - scrollY / 300)),
              transition: 'none', // Disable transitions for smooth parallax
              willChange: 'transform, opacity', // Optimize for animations
            }}
          >
{content.hero_subtitle}
          </Typography>

        </Container>
      </HeroSection>

      {/* Mission Statement */}
      <FeatureSection maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
        <Typography 
          variant="h2" 
          component="h2" 
          align="center" 
          gutterBottom 
          sx={{ 
            fontFamily: 'var(--font-family-heading)',
            fontWeight: 'var(--font-weight-bold)',
            fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            color: 'var(--color-text-primary)',
            mb: 'var(--spacing-md)',
            lineHeight: 'var(--line-height-tight)',
            letterSpacing: 'var(--letter-spacing-tight)'
          }}
        >
{content.mission_title}
        </Typography>
        <Typography 
          variant="body1" 
          component="p" 
          align="center" 
          sx={{ 
            fontFamily: 'var(--font-family-base)',
            fontSize: 'var(--font-size-xl)',
            color: 'var(--color-text-secondary)',
            lineHeight: 'var(--line-height-relaxed)',
            mb: 'var(--spacing-xl)', 
            maxWidth: '900px', 
            mx: 'auto',
            fontWeight: 'var(--font-weight-normal)'
          }}
        >
{content.mission_description}
        </Typography>
      </FeatureSection>

      {/* Dashboard Preview */}
      <ContentSection alternate>
        <Container maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontFamily: 'var(--font-family-heading)',
              fontWeight: 'var(--font-weight-bold)',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              color: 'var(--color-text-primary)',
              mb: 'var(--spacing-md)',
              lineHeight: 'var(--line-height-tight)',
              letterSpacing: 'var(--letter-spacing-tight)'
            }}
          >
            Situation Room in action
          </Typography>
          <Typography 
            variant="body1" 
            component="p" 
            align="center" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--line-height-relaxed)',
              mb: 'var(--spacing-lg)', 
              maxWidth: '800px', 
              mx: 'auto',
              fontWeight: 'var(--font-weight-normal)'
            }}
          >
            Explore our live situation rooms featuring real-time data and AI-driven analysis. See how we monitor context, shocks, displacement, humanitarian access, impact and conditions in one unified view.
          </Typography>

          {/* Dashboard Preview Tabs */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 'var(--spacing-sm)',
            mb: 'var(--spacing-lg)'
          }}>
            {[
              { key: 'myanmar', label: 'Myanmar' },
              { key: 'eastern-ukraine', label: 'Eastern Ukraine' },
            ].map((tab) => (
              <Button
                key={tab.key}
                variant={selectedPreview === tab.key ? 'contained' : 'outlined'}
                onClick={() => setSelectedPreview(tab.key)}
                sx={{
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 'var(--font-weight-semibold)',
                  fontSize: 'var(--font-size-base)',
                  textTransform: 'none',
                  borderRadius: '6px',
                  px: 'var(--spacing-lg)',
                  py: 'var(--spacing-sm)',
                  transition: 'all 0.2s ease',
                  ...(selectedPreview === tab.key ? {
                    backgroundColor: 'var(--color-primary)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0, 95, 148, 0.25)',
                    '&:hover': {
                      backgroundColor: 'var(--color-secondary)',
                    }
                  } : {
                    borderColor: 'var(--color-primary)',
                    color: 'var(--color-primary)',
                    '&:hover': {
                      backgroundColor: 'var(--color-action-hover)',
                      borderColor: 'var(--color-secondary)',
                    }
                  })
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>
        
          {/* Demo notice */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 'var(--spacing-md)' }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              px: '12px',
              py: '5px',
              borderRadius: '20px',
              border: '1px solid rgba(0, 95, 148, 0.15)',
              backgroundColor: 'rgba(0, 95, 148, 0.04)',
              fontSize: '0.78rem',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-family-body)',
            }}>
              ⓘ These dashboards are snapshot demos. Live premium dashboards continuously update automatically on a schedule according to the type of situation and pricing plan.
            </Box>
          </Box>

          {/* Dashboard Container */}
          <Box ref={dashboardRef} sx={{
            position: 'relative',
            width: '100%',
            height: 'calc(1038.9610389610389px * 0.77)', // Match the scaled iframe height
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0, 95, 148, 0.15)',
            border: '1px solid rgba(0, 95, 148, 0.1)',
            overflow: 'hidden' // Hide any overflow from the iframe
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden', // This will crop the scaled iframe
              borderRadius: '8px'
            }}>
              <iframe 
                ref={iframeRef}
                src={`${window.location.origin}/public/global/${selectedPreview}?eventsCollapsed=false&eventsWidth=25&showHeader=true&view=${selectedPreview}&theme=situation-room-default&hideExport=true`}
                frameBorder="0" 
                style={{
                  width: 'calc((100%)*1.2987012987012987)', 
                  height: '1038.9610389610389px', 
                  transform: 'scale(0.77)', 
                  transformOrigin: '0 0',
                  borderRadius: '8px'
                }}
                allowFullScreen
                title={`Situation Room Dashboard - ${selectedPreview === 'myanmar' ? 'Myanmar' : 'Eastern Ukraine'}`}
              />
            </Box>
            <OverlayContainer>
              {/* 1. Select a Pillar/Sector */}
              <OverlayBox 
                ref={el => overlayBoxesRef.current[0] = el}
                sx={{ 
                  top: 'calc(-2% + 50px)', 
                  left: 'calc(46% - 150px)', 
                  transform: 'translateX(-50%)'
                }}
              >
                <OverlayHeader>
                  <OverlayNumber>1</OverlayNumber>
                  <DashboardCustomizeOutlinedIcon sx={{ color: 'var(--color-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'var(--font-weight-semibold)', fontSize: '1rem' }}>Pillars & Sectors</Typography>
                </OverlayHeader>
                <Typography variant="body2" color="text.secondary" sx={{ pl: 'calc(28px + var(--spacing-sm))' }}>
                  Click a data card to load its detailed analysis.
                </Typography>
              </OverlayBox>

              {/* 2. Drill Down Further */}
              <OverlayBox 
                ref={el => overlayBoxesRef.current[1] = el}
                sx={{ 
                  top: 'calc(28% + 75px)', 
                  left: '14%', 
                  transform: 'translateX(-50%)'
                }}
              >
                <OverlayHeader>
                  <OverlayNumber>2</OverlayNumber>
                  <PlagiarismOutlinedIcon sx={{ color: 'var(--color-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'var(--font-weight-semibold)', fontSize: '1rem' }}>Detailed Analysis</Typography>
                </OverlayHeader>
                <Typography variant="body2" color="text.secondary" sx={{ pl: 'calc(28px + var(--spacing-sm))' }}>
                  Explore sub-pillars and drivers for deeper insights.
                </Typography>
              </OverlayBox>

              {/* 3. Analyze the Map */}
              <OverlayBox 
                ref={el => overlayBoxesRef.current[2] = el}
                sx={{ 
                  bottom: '17%', 
                  left: 'calc(51% - 150px)', 
                  transform: 'translateX(-50%)'
                }}
              >
                <OverlayHeader>
                  <OverlayNumber>3</OverlayNumber>
                  <TravelExploreOutlinedIcon sx={{ color: 'var(--color-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'var(--font-weight-semibold)', fontSize: '1rem' }}>Interactive Map</Typography>
                </OverlayHeader>
                <Typography variant="body2" color="text.secondary" sx={{ pl: 'calc(28px + var(--spacing-sm))' }}>
                  Filter data by region and uncover local insights.
                </Typography>
              </OverlayBox>

              {/* 5. Track Recent Events */}
              <OverlayBox 
                ref={el => overlayBoxesRef.current[4] = el}
                sx={{ 
                  top: 'calc(65% - 150px)', 
                  right: 'calc(2% + 50px)', 
                  transform: 'translateX(0)'
                }}
              >
                <OverlayHeader>
                  <OverlayNumber>5</OverlayNumber>
                  <FeedOutlinedIcon sx={{ color: 'var(--color-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'var(--font-weight-semibold)', fontSize: '1rem' }}>Events Feed</Typography>
                </OverlayHeader>
                <Typography variant="body2" color="text.secondary" sx={{ pl: 'calc(28px + var(--spacing-sm))' }}>
                  Review a live feed of recent events and their impact.
                </Typography>
              </OverlayBox>

              {/* 4. KPI Pagination */}
              <OverlayBox 
                ref={el => overlayBoxesRef.current[3] = el}
                sx={{ 
                  top: 'calc(8% + 20px)', 
                  right: 'calc(8% + 20px)', 
                  transform: 'translateX(0)'
                }}
              >
                <OverlayHeader>
                  <OverlayNumber>4</OverlayNumber>
                  <TrendingUpIcon sx={{ color: 'var(--color-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'var(--font-weight-semibold)', fontSize: '1rem' }}>Browse KPIs</Typography>
                </OverlayHeader>
                <Typography variant="body2" color="text.secondary" sx={{ pl: 'calc(28px + var(--spacing-sm))' }}>
                  Click the arrows to paginate through different sectors and KPI boxes.
                </Typography>
              </OverlayBox>

              {/* 6. AI Assistant */}
              <OverlayBox 
                ref={el => overlayBoxesRef.current[5] = el}
                sx={{ 
                  bottom: '5%', 
                  right: 'calc(2% + 50px)', 
                  transform: 'translateX(0)'
                }}
              >
                <OverlayHeader>
                  <OverlayNumber>6</OverlayNumber>
                  <PsychologyIcon sx={{ color: 'var(--color-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'var(--font-weight-semibold)', fontSize: '1rem' }}>AI Assistant</Typography>
                </OverlayHeader>
                <Typography variant="body2" color="text.secondary" sx={{ pl: 'calc(28px + var(--spacing-sm))' }}>
                  Chat with AI to ask questions about the analysis, data and trends.
                </Typography>
              </OverlayBox>
            </OverlayContainer>
          </Box>
          {/* Explore Other Views - HIDDEN */}
          {/*
          <Box id="view-selection" sx={{ 
            mt: 'var(--spacing-xl)',
            pt: 'var(--spacing-lg)',
            borderTop: '1px solid rgba(0, 95, 148, 0.1)',
{{ ... }}
            position: 'relative'
          }}>
            <Typography 
              variant="h3" 
              component="h3" 
              align="center" 
              gutterBottom 
              sx={{ 
                fontFamily: 'var(--font-family-heading)',
                fontWeight: 'var(--font-weight-bold)',
                fontSize: 'var(--font-size-xl)',
                color: 'var(--color-text-primary)',
                mb: 'var(--spacing-sm)',
                lineHeight: 'var(--line-height-tight)'
              }}
            >
              Explore Other Views
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              sx={{ 
                fontFamily: 'var(--font-family-base)',
                fontSize: 'var(--font-size-lg)',
                color: 'var(--color-text-secondary)',
                lineHeight: 'var(--line-height-relaxed)',
                mb: 'var(--spacing-lg)',
                fontWeight: 'var(--font-weight-normal)'
              }}
            >
              Access full situation room dashboards for other views
            </Typography>

            {isLoading ? (
              <Box display="flex" justifyContent="center" sx={{ py: 'var(--spacing-xl)' }}>
                <CircularProgress size={50} sx={{ color: 'var(--color-primary)' }} />
              </Box>
            ) : error ? (
              <Alert 
                severity="error" 
                sx={{ 
                  maxWidth: 500, 
                  mx: 'auto',
                  borderRadius: '6px',
                  '& .MuiAlert-message': {
                    fontFamily: 'var(--font-family-base)',
                    fontSize: 'var(--font-size-lg)'
                  }
                }}
              >
                {error}
              </Alert>
            ) : (
              <Grid container spacing={3} sx={{ 
                '& .MuiGrid-item': {
                  opacity: 0,
                  transform: 'translateY(20px)',
                  animation: 'fadeInUp 0.6s ease forwards',
                  '&:nth-of-type(1)': { animationDelay: '0.1s' },
                  '&:nth-of-type(2)': { animationDelay: '0.2s' },
                  '&:nth-of-type(3)': { animationDelay: '0.3s' },
                  '&:nth-of-type(4)': { animationDelay: '0.4s' },
                  '&:nth-of-type(n+5)': { animationDelay: '0.5s' },
                },
                '@keyframes fadeInUp': {
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  }
                }
              }}>
                {views.map((view) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={view.iso3}>
                      <ViewCard onClick={() => handleViewSelect(view.iso3)}>
                        <CardActionArea sx={{ height: '100%' }}>
                          <CardContent sx={{ 
                            p: 'var(--spacing-md)', 
                            textAlign: 'center',
                            transition: 'all 0.2s ease'
                          }}>
                          <Typography 
                            variant="h6" 
                            component="h3"
                            sx={{
                              fontFamily: 'var(--font-family-heading)',
                              fontWeight: 'var(--font-weight-semibold)',
                              fontSize: 'var(--font-size-lg)',
                              color: 'var(--color-text-primary)',
                              mb: 'var(--spacing-xs)'
                            }}
                          >
                            {view.name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{
                              fontFamily: 'var(--font-family-base)',
                              fontSize: 'var(--font-size-md)',
                              color: 'var(--color-text-secondary)',
                              fontWeight: 'var(--font-weight-medium)'
                            }}
                          >
                            {view.iso3}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </ViewCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
          */}
        </Container>
      </ContentSection>

      {/* The Situation Room Analysis Workflow */}
      <Box sx={{ 
        background: 'linear-gradient(180deg, rgba(0, 95, 148, 0.02) 0%, var(--color-background) 100%)',
        py: 'var(--spacing-md)',
        mt: 'var(--spacing-md)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '400px',
          height: '3px',
          background: 'linear-gradient(90deg, transparent 0%, var(--color-primary) 15%, var(--color-secondary) 85%, transparent 100%)',
          borderRadius: '2px',
          opacity: 0.4,
        }
      }}>
        <FeatureSection maxWidth={false} sx={{ 
          maxWidth: '1320px', 
          mx: 'auto', 
          px: 3,
          pt: 'var(--spacing-lg) !important'
        }}>
          <Typography 
            variant="h2" 
            component="h2" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontFamily: 'var(--font-family-heading)',
              fontWeight: 'var(--font-weight-bold)',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              color: 'var(--color-text-primary)',
              mb: 'var(--spacing-xl)',
              lineHeight: 'var(--line-height-tight)',
              letterSpacing: 'var(--letter-spacing-tight)'
            }}
          >
            {content.workflow_section_title}
          </Typography>
          <PipelineContainer>
            <PipelineFlow>
              {workflowSteps.map((step, index) => (
                <PipelineStage
                  key={index}
                  isLast={index === workflowSteps.length - 1}
                  ref={el => workflowRefs.current[index] = el}
                  className={visibleWorkflowCards.includes(index) ? 'workflow-card-visible' : 'workflow-card-hidden'}
                  sx={{
                    transition: 'opacity 0.4s cubic-bezier(0.4,0,0.2,1), transform 0.4s cubic-bezier(0.4,0,0.2,1)', // Faster: reduced from 0.7s to 0.4s
                    transitionDelay: visibleWorkflowCards.includes(index) ? `${index * 40}ms` : '0ms', // Faster: reduced from 80ms to 40ms
                    opacity: visibleWorkflowCards.includes(index) ? 1 : 0,
                    transform: visibleWorkflowCards.includes(index)
                      ? 'translateY(0) scale(1)'
                      : 'translateY(20px) scale(0.99)', // More subtle: reduced from 32px and 0.98 to 20px and 0.99
                    zIndex: 2,
                  }}
                >
                  <PipelineIconContainer className="pipeline-icon">
                    <step.icon sx={{ 
                      fontSize: { xs: '1.5rem', sm: '2rem' }, 
                      color: 'var(--color-background-paper)' 
                    }} />
                  </PipelineIconContainer>
                  <PipelineTitle>
                    {step.title}
                  </PipelineTitle>
                  <PipelineDescription>
                    {step.description}
                  </PipelineDescription>
                </PipelineStage>
              ))}
            </PipelineFlow>
          </PipelineContainer>
        </FeatureSection>
      </Box>

      {/* Custom Situation Room Solutions */}
      <ContentSection alternate>
        <Container maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontFamily: 'var(--font-family-heading)',
              fontWeight: 'var(--font-weight-bold)',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              color: 'var(--color-text-primary)',
              mb: 'var(--spacing-md)',
              lineHeight: 'var(--line-height-tight)',
              letterSpacing: 'var(--letter-spacing-tight)'
            }}
          >
            {content.custom_solution_title}
          </Typography>
          
          <Typography 
            variant="body1" 
            component="p" 
            align="center" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--line-height-relaxed)',
              mb: 'var(--spacing-lg)', 
              maxWidth: '1000px', 
              mx: 'auto',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
{content.customization_title}
          </Typography>

          <Typography 
            variant="body1" 
            component="p" 
            align="center" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-lg)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--line-height-relaxed)',
              mb: 'var(--spacing-lg)', 
              maxWidth: '1000px', 
              mx: 'auto',
              fontWeight: 'var(--font-weight-normal)'
            }}
          >
{content.customization_description}
          </Typography>

          <Typography 
            variant="body1" 
            component="p" 
            align="center" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-lg)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--line-height-relaxed)',
              mb: 'var(--spacing-xl)', 
              maxWidth: '900px', 
              mx: 'auto',
              fontWeight: 'var(--font-weight-normal)'
            }}
          >
{content.customization_conclusion}
          </Typography>
          
          <TechGridContainer>
            <TechGrid>
              {customSolutionSteps.map((step, index) => (
                <TechCard key={index}>
                  <TechBadge className="tech-badge">
                    {String(index + 1).padStart(2, '0')}
                  </TechBadge>
                  
                  <TechTitle>
                    {step.title}
                  </TechTitle>
                  
                  <TechDescription>
                    {step.description}
                  </TechDescription>
                  
                  <Typography 
                    variant="body2" 
                    sx={{
                      fontFamily: 'var(--font-family-base)',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-primary)',
                      lineHeight: 'var(--line-height-normal)',
                      mb: 'var(--spacing-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    We take care of:
                  </Typography>
                  
                  <TechFeatureList>
                    {step.details.map((detail, detailIndex) => (
                      <Box key={detailIndex} className="tech-feature">
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'var(--font-family-base)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            lineHeight: 'var(--line-height-normal)',
                            fontWeight: 'var(--font-weight-normal)'
                          }}
                        >
                          {detail}
                        </Typography>
                      </Box>
                    ))}
                  </TechFeatureList>
                </TechCard>
              ))}
            </TechGrid>
          </TechGridContainer>
        </Container>
      </ContentSection>

      {/* Analysis Framework */}
      <ContentSection alternate>
        <Container maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontFamily: 'var(--font-family-heading)',
              fontWeight: 'var(--font-weight-bold)',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              color: 'var(--color-text-primary)',
              mb: 'var(--spacing-md)',
              lineHeight: 'var(--line-height-tight)',
              letterSpacing: 'var(--letter-spacing-tight)'
            }}
          >
{content.analysis_framework_title}
          </Typography>
          <Typography 
            variant="body1" 
            component="p" 
            align="center" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--line-height-relaxed)',
              mb: 'var(--spacing-xl)', 
              maxWidth: '900px', 
              mx: 'auto',
              fontWeight: 'var(--font-weight-normal)'
            }}
          >
{content.analysis_framework_description}
          </Typography>
          
          <Grid container spacing={4} alignItems="center">
            {/* Framework SVG */}
            <Grid item xs={12} md={5}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center',
                p: 'var(--spacing-md)'
              }}>
                <Typography 
                  variant="h3" 
                  component="h3" 
                  align="center" 
                  gutterBottom 
                  sx={{ 
                    fontFamily: 'var(--font-family-heading)',
                    fontWeight: 'var(--font-weight-bold)',
                    fontSize: 'var(--font-size-xl)',
                    color: 'var(--color-text-primary)',
                    mb: 'var(--spacing-lg)',
                    lineHeight: 'var(--line-height-tight)'
                  }}
                >
                  {content.framework_section_title}
                </Typography>
                <Box
                  component="img"
                  src="/framework.svg"
                  alt="Analytical Framework Diagram"
                  onClick={() => setFrameworkModalOpen(true)}
                  sx={{
                    width: '100%',
                    maxWidth: '400px',
                    height: 'auto',
                    borderRadius: '8px',
                    filter: 'grayscale(100%)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    objectFit: 'contain',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      filter: 'grayscale(0%)',
                      transform: 'scale(1.02)',
                      boxShadow: '0 4px 20px rgba(0, 95, 148, 0.15)',
                    }
                  }}
                />
                <Typography 
                  variant="caption" 
                  align="center" 
                  sx={{ 
                    fontFamily: 'var(--font-family-base)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    mt: 'var(--spacing-sm)',
                    fontStyle: 'italic',
                    cursor: 'pointer'
                  }}
                  onClick={() => setFrameworkModalOpen(true)}
                >
                  Click to enlarge
                </Typography>
              </Box>
            </Grid>
            
            {/* Five Pillars Content */}
            <Grid item xs={12} md={7}>
          <CleanAnalysisContainer>
            {analysisLevels.map((level, index) => (
              <CleanAnalysisItem
                key={index}
                ref={el => analysisRefs.current[index] = el}
                className={visibleAnalysisCards.includes(index) ? 'analysis-card-visible' : 'analysis-card-hidden'}
                sx={{
                  transition: 'opacity 0.4s cubic-bezier(0.4,0,0.2,1), transform 0.4s cubic-bezier(0.4,0,0.2,1)', // Faster: reduced from 0.7s to 0.4s
                  transitionDelay: visibleAnalysisCards.includes(index) ? `${index * 40}ms` : '0ms', // Faster: reduced from 80ms to 40ms
                  opacity: visibleAnalysisCards.includes(index) ? 1 : 0,
                  transform: visibleAnalysisCards.includes(index)
                    ? 'translateY(0) scale(1)'
                    : 'translateY(20px) scale(0.99)', // More subtle: reduced from 32px and 0.98 to 20px and 0.99
                }}
              >
                <CleanAnalysisHeader>
                  <CleanAnalysisNumber>
                    {index + 1}
                  </CleanAnalysisNumber>
                  <CleanAnalysisTitle>
                    {level.level}
                  </CleanAnalysisTitle>
                </CleanAnalysisHeader>
                <CleanAnalysisDescription>
                  {level.description}
                </CleanAnalysisDescription>
              </CleanAnalysisItem>
            ))}
          </CleanAnalysisContainer>
            </Grid>
          </Grid>
          
          <Typography 
            variant="body1" 
            component="p" 
            align="center" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-lg)',
              color: 'var(--color-text-secondary)',
              fontStyle: 'italic',
              mt: 'var(--spacing-lg)',
              mb: 'var(--spacing-md)'
            }}
          >
            Each analysis includes tables, maps, problem trees, and timelines for deeper insights.
          </Typography>
          
          <Typography 
            variant="caption" 
            component="p" 
            align="center" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              fontStyle: 'italic',
              mt: 'var(--spacing-sm)',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 'var(--line-height-relaxed)'
            }}
          >
            ({content.framework_tested_text})
          </Typography>
        </Container>
      </ContentSection>

      {/* How do we help Section */}
      <FeatureSection maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
        <Typography 
          variant="h2" 
          component="h2" 
          align="center" 
          gutterBottom 
          sx={{ 
            fontFamily: 'var(--font-family-heading)',
            fontWeight: 'var(--font-weight-bold)',
            fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            color: 'var(--color-text-primary)',
            mb: 'var(--spacing-md)',
            lineHeight: 'var(--line-height-tight)',
            letterSpacing: 'var(--letter-spacing-tight)'
          }}
        >
          How do we help?
        </Typography>
        <Typography 
          variant="body1" 
          component="p" 
          align="center" 
          sx={{ 
            fontFamily: 'var(--font-family-base)',
            fontSize: 'var(--font-size-xl)',
            color: 'var(--color-text-secondary)',
            lineHeight: 'var(--line-height-relaxed)',
            mb: 'var(--spacing-xl)', 
            maxWidth: '800px', 
            mx: 'auto',
            fontWeight: 'var(--font-weight-normal)'
          }}
        >
          Whether you're planning a needs assessment, designing a response strategy, or coordinating operations, 
          the Situation Room delivers timely, actionable intelligence — at a fraction of the cost and time of traditional human-only analysis.
        </Typography>
      </FeatureSection>

      {/* Our Services & Pricing Combined */}
      <FeatureSection 
        maxWidth={false} 
        sx={{ 
          maxWidth: '1320px', 
          mx: 'auto', 
          px: 3,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(0, 95, 148, 0.02) 0%, rgba(0, 109, 135, 0.03) 50%, rgba(0, 95, 148, 0.02) 100%)',
            borderRadius: '24px',
            zIndex: 0,
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          }
        }}
      >
        {/* Unified Section Header */}
        <Box sx={{ textAlign: 'center', mb: 'var(--spacing-xl)' }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontFamily: 'var(--font-family-heading)',
              fontWeight: 'var(--font-weight-bold)',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              color: 'var(--color-text-primary)',
              mb: 'var(--spacing-sm)',
              lineHeight: 'var(--line-height-tight)',
              letterSpacing: 'var(--letter-spacing-tight)'
            }}
          >
            Services
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-secondary)',
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 'var(--line-height-relaxed)'
            }}
          >
            {content.services_pricing_subtitle}
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="flex-start" justifyContent="center">
          {/* Left Column - Our Services */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 'var(--spacing-lg)',
              gap: 'var(--spacing-md)'
            }}>
              <Typography 
                variant="h3" 
                component="h3" 
                sx={{ 
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 'var(--font-weight-bold)',
                  fontSize: 'var(--font-size-xl)',
                  color: 'var(--color-text-primary)',
                  lineHeight: 'var(--line-height-tight)'
                }}
              >
                Our Services
              </Typography>
              <Box sx={{
                height: '3px',
                flex: 1,
                background: 'linear-gradient(90deg, var(--color-primary) 0%, transparent 100%)',
                borderRadius: '2px'
              }} />
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CleanServiceContainer>
                  <CleanServiceBadge className="service-badge">Standard</CleanServiceBadge>
                  <Box sx={{ p: 'var(--spacing-xl)', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    <CleanServiceIconWrapper className="service-icon">
                      <AnalyticsIcon />
                    </CleanServiceIconWrapper>
                    <div className="service-content">
                      <Typography 
                        variant="h4" 
                        component="h3" 
                        sx={{
                          fontFamily: 'var(--font-family-heading)',
                          fontWeight: 'var(--font-weight-bold)',
                          fontSize: 'var(--font-size-lg)',
                          color: 'var(--color-text-primary)',
                          mb: 'var(--spacing-sm)',
                          lineHeight: 'var(--line-height-tight)'
                        }}
                      >
                        Ready-to-Use Situation Room and View Analyses
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{
                          fontFamily: 'var(--font-family-base)',
                          fontSize: 'var(--font-size-md)',
                          color: 'var(--color-text-secondary)',
                          lineHeight: 'var(--line-height-relaxed)',
                          mb: 'var(--spacing-md)'
                        }}
                      >
                        Get weekly situation and risk reports for the views you select, featuring full five-level analysis across the operational context and sectoral pillars. Your subscription gives all team members with an official organizational email access to the online Situation Room for the duration of your contract.
                      </Typography>
                      
                      <CleanServiceFeatureList>
                        <Box className="feature-item">
                          <CheckCircleIcon className="feature-icon" />
                          <span>Weekly automated reports</span>
                        </Box>
                        <Box className="feature-item">
                          <CheckCircleIcon className="feature-icon" />
                          <span>Full team access with org email</span>
                        </Box>
                        <Box className="feature-item">
                          <CheckCircleIcon className="feature-icon" />
                          <span>Five-level analysis framework</span>
                        </Box>
                        <Box className="feature-item">
                          <CheckCircleIcon className="feature-icon" />
                          <span>All humanitarian sectors covered</span>
                        </Box>
                      </CleanServiceFeatureList>
                    </div>
                  </Box>
                </CleanServiceContainer>
              </Grid>
              
              <Grid item xs={12}>
                <CleanServiceContainer>
                  <CleanServiceBadge className="service-badge">Custom</CleanServiceBadge>
                  <Box sx={{ p: 'var(--spacing-xl)', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    <CleanServiceIconWrapper className="service-icon">
                      <PsychologyIcon />
                    </CleanServiceIconWrapper>
                    <div className="service-content">
                      <Typography 
                        variant="h4" 
                        component="h3" 
                        sx={{
                          fontFamily: 'var(--font-family-heading)',
                          fontWeight: 'var(--font-weight-bold)',
                          fontSize: 'var(--font-size-lg)',
                          color: 'var(--color-text-primary)',
                          mb: 'var(--spacing-sm)',
                          lineHeight: 'var(--line-height-tight)'
                        }}
                      >
                        Custom Situation Room Setup
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{
                          fontFamily: 'var(--font-family-base)',
                          fontSize: 'var(--font-size-md)',
                          color: 'var(--color-text-secondary)',
                          lineHeight: 'var(--line-height-relaxed)',
                          mb: 'var(--spacing-md)'
                        }}
                      >
                        We configure every Situation Room to match your specific goals, workflows, and analytical use cases. Whether you're working in policy, research, private sector intelligence, or field-based operations, our team delivers a fully customized AI-powered platform that fits your context.
                      </Typography>
                      
                      <CleanServiceFeatureList>
                        <Box className="feature-item">
                          <CheckCircleIcon className="feature-icon" />
                          <span>Tailored analysis frameworks</span>
                        </Box>
                        <Box className="feature-item">
                          <CheckCircleIcon className="feature-icon" />
                          <span>Custom data sources integration</span>
                        </Box>
                        <Box className="feature-item">
                          <CheckCircleIcon className="feature-icon" />
                          <span>Specialized AI prompts & workflows</span>
                        </Box>
                        <Box className="feature-item">
                          <CheckCircleIcon className="feature-icon" />
                          <span>Dedicated implementation support</span>
                        </Box>
                      </CleanServiceFeatureList>
                    </div>
                  </Box>
                </CleanServiceContainer>
              </Grid>
            </Grid>
          </Grid>

{/* Right Column - Pricing - TEMPORARILY HIDDEN
          <Grid item xs={12} lg={7}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 'var(--spacing-lg)',
              gap: 'var(--spacing-md)'
            }}>
              <Typography 
                variant="h3" 
                component="h3" 
                sx={{ 
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 'var(--font-weight-bold)',
                  fontSize: 'var(--font-size-xl)',
                  color: 'var(--color-text-primary)',
                  lineHeight: 'var(--line-height-tight)'
                }}
              >
                {content.pricing_column_title}
              </Typography>
              <Box sx={{
                height: '3px',
                flex: 1,
                background: 'linear-gradient(90deg, var(--color-secondary) 0%, transparent 100%)',
                borderRadius: '2px'
              }} />
            </Box>
            
            <CleanPricingContainer>
              <CleanPricingTable>
                <CleanPricingHeader>
                  <CleanHeaderCell>Plans</CleanHeaderCell>
                  {pricingPlans.map((plan, index) => (
                    <CleanHeaderCell key={index}>
                      {plan.plan}
                      {index === 0 && <CleanPlanBadge>Most Popular</CleanPlanBadge>}
                    </CleanHeaderCell>
                  ))}
                </CleanPricingHeader>

                <CleanPricingBody>
                  <CleanRowLabel>Monthly</CleanRowLabel>
                  {pricingPlans.map((plan, index) => (
                    <CleanPriceCell key={`monthly-${index}`} isPopular={index === 0}>
                      <MobilePlanHeader>
                        <MobilePlanTitle isPopular={index === 0}>
                          {plan.plan}
                        </MobilePlanTitle>
                        {index === 0 && <CleanPlanBadge>Most Popular</CleanPlanBadge>}
                      </MobilePlanHeader>
                      
                      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                        <PriceAmount>{plan.monthly}</PriceAmount>
                      </Box>

                      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                        <PriceDisplay>
                          <PriceAmount>{plan.monthly}</PriceAmount>
                          <PriceLabel>per month</PriceLabel>
                        </PriceDisplay>

                        <PricingSeparator />
                        
                        <PriceDisplay>
                        {index === 3 ? (
                          <Typography 
                            variant="body2" 
                            sx={{
                              fontFamily: 'var(--font-family-base)',
                              fontSize: 'var(--font-size-sm)',
                              fontWeight: 'var(--font-weight-normal)',
                              color: 'var(--color-text-primary)',
                              lineHeight: 'var(--line-height-normal)',
                              textAlign: 'center'
                            }}
                          >
                            {plan.yearly}
                          </Typography>
                        ) : (
                          <>
                            <PriceAmount style={{ fontSize: 'var(--font-size-lg)' }}>
                              {plan.yearly}
                            </PriceAmount>
                            <PriceLabel>per year</PriceLabel>
                          </>
                        )}
                      </PriceDisplay>
                      </Box>
                    </CleanPriceCell>
                  ))}

                  <CleanRowLabel>Yearly</CleanRowLabel>
                  {pricingPlans.map((plan, index) => (
                    <CleanYearlyPriceCell 
                      key={`yearly-${index}`} 
                      isPopular={index === 0}
                    >
                      {index === 3 ? (
                        <Typography 
                          variant="body2" 
                          sx={{
                            fontFamily: 'var(--font-family-base)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-normal)',
                            color: 'var(--color-text-primary)',
                            lineHeight: 'var(--line-height-normal)',
                            textAlign: 'center'
                          }}
                        >
                          {plan.yearly}
                        </Typography>
                      ) : (
                        <PriceAmount>{plan.yearly}</PriceAmount>
                      )}
                    </CleanYearlyPriceCell>
                  ))}

                </CleanPricingBody>
              </CleanPricingTable>
            </CleanPricingContainer>

            <Typography 
              variant="h4" 
              gutterBottom
              sx={{
                fontFamily: 'var(--font-family-heading)',
                fontWeight: 'var(--font-weight-bold)',
                fontSize: 'var(--font-size-lg)',
                color: 'var(--color-text-primary)',
                mb: 'var(--spacing-md)',
                textAlign: 'left',
                mt: 'var(--spacing-lg)'
              }}
            >
              What's Included in All Plans
            </Typography>
            <CleanFeatureGrid>
              {includedFeatures.map((feature, index) => (
                <CleanFeatureItem key={index}>
                  <CheckCircleIcon sx={{ color: 'var(--color-secondary)', fontSize: '1.1rem', flexShrink: 0 }} />
                  <Typography 
                    variant="body1"
                    sx={{
                      fontFamily: 'var(--font-family-base)',
                      fontSize: 'var(--font-size-md)',
                      color: 'var(--color-text-primary)',
                      fontWeight: 'var(--font-weight-medium)',
                      lineHeight: 'var(--line-height-tight)'
                    }}
                  >
                    {feature}
                  </Typography>
                </CleanFeatureItem>
              ))}
            </CleanFeatureGrid>

            <Box sx={{ 
              mt: 'var(--spacing-xl)', 
              p: 'var(--spacing-lg)',
              backgroundColor: 'rgba(0, 95, 148, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(0, 95, 148, 0.1)',
              textAlign: 'center'
            }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'var(--font-family-base)',
                  fontSize: 'var(--font-size-md)',
                  color: 'var(--color-text-secondary)',
                  mb: 'var(--spacing-md)'
                }}
              >
                {content.pricing_text}
              </Typography>
              <ProfessionalButton
                onClick={scrollToContact}
                sx={{
                  display: 'inline-block',
                  fontSize: 'var(--font-size-md)',
                  padding: 'var(--spacing-sm) var(--spacing-lg)',
                  cursor: 'pointer'
                }}
              >
                {content.contact_button_text}
              </ProfessionalButton>
            </Box>
          </Grid>
          END TEMPORARILY HIDDEN */}
        </Grid>
      </FeatureSection>

      {/* Our contributors Section - COMMENTED OUT */}
      {/*
      <FeatureSection maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
        <Typography 
          variant="h2" 
          component="h2" 
          align="center" 
          gutterBottom 
          sx={{ 
            fontFamily: 'var(--font-family-heading)',
            fontWeight: 'var(--font-weight-bold)',
            fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            color: 'var(--color-text-primary)',
            mb: 'var(--spacing-md)',
            lineHeight: 'var(--line-height-tight)',
            letterSpacing: 'var(--letter-spacing-tight)'
          }}
        >
          Our contributors
        </Typography>
        <Typography 
          variant="body1" 
          component="p" 
          align="center" 
          sx={{ 
            fontFamily: 'var(--font-family-base)',
            fontSize: 'var(--font-size-xl)',
            color: 'var(--color-text-secondary)',
            lineHeight: 'var(--line-height-relaxed)',
            mb: 'var(--spacing-xl)', 
            maxWidth: '600px', 
            mx: 'auto',
            fontWeight: 'var(--font-weight-normal)'
          }}
        >
          We are a collective of humanitarian analysts, data engineers, and developers with a single mission: 
          to deliver trusted, actionable information to those saving lives.
        </Typography>
      </FeatureSection>
      */}

      {/* Our Team */}
      <FeatureSection maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
        <Typography 
          variant="h2" 
          component="h2" 
          align="center" 
          gutterBottom 
          sx={{ 
            fontFamily: 'var(--font-family-heading)',
            fontWeight: 'var(--font-weight-bold)',
            fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            color: 'var(--color-text-primary)',
            mb: 'var(--spacing-md)',
            lineHeight: 'var(--line-height-tight)',
            letterSpacing: 'var(--letter-spacing-tight)'
          }}
        >
          {content.team_title}
        </Typography>
        <Typography 
          variant="body1" 
          component="p" 
          align="center" 
          sx={{ 
            fontFamily: 'var(--font-family-base)',
            fontSize: 'var(--font-size-xl)',
            color: 'var(--color-text-secondary)',
            lineHeight: 'var(--line-height-relaxed)',
            mb: 'var(--spacing-xl)', 
            maxWidth: '800px', 
            mx: 'auto',
            fontWeight: 'var(--font-weight-normal)'
          }}
        >
          {content.team_description}
        </Typography>
        
        <Box sx={{ maxWidth: '1000px', mx: 'auto' }}>
          {/* Patrice Chataigner */}
          <MinimalProfileContainer>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: 'var(--spacing-xl)',
              alignItems: { xs: 'center', md: 'flex-start' }
            }}>
              {/* Photo and Contact Buttons */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 'var(--spacing-md)',
                flexShrink: 0
              }}>
                <Box sx={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '3px',
                  border: '1px solid var(--color-action-disabled-background)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <Box
                    component="img"
                    src="/team/Patrice Chataigner.png"
                    alt="Patrice Chataigner"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center 20%',
                      filter: 'grayscale(100%)',
                      transition: 'filter 0.3s ease',
                      '&:hover': {
                        filter: 'grayscale(0%)'
                      }
                    }}
                  />
                </Box>
                
                {/* Contact Buttons */}
                <Box sx={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <Box 
                    component="a"
                    href={`mailto:${content.patrice_email}`}
                    sx={{
                      width: '36px',
                      height: '36px',
                      border: '1px solid var(--color-action-disabled-background)',
                      backgroundColor: 'var(--color-background-paper)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      borderRadius: '3px',
                      '&:hover': {
                        borderColor: 'var(--color-primary)',
                        backgroundColor: 'var(--color-primary-5)',
                      }
                    }}
                  >
                    <EmailIcon sx={{ fontSize: '1rem', color: 'var(--color-text-secondary)' }} />
                  </Box>
                  <Box 
                    component="a"
                    href={content.patrice_linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: '36px',
                      height: '36px',
                      border: '1px solid var(--color-action-disabled-background)',
                      backgroundColor: 'var(--color-background-paper)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      borderRadius: '3px',
                      '&:hover': {
                        borderColor: 'var(--color-primary)',
                        backgroundColor: 'var(--color-primary-5)',
                      }
                    }}
                  >
                    <LinkedInIcon sx={{ fontSize: '1rem', color: 'var(--color-text-secondary)' }} />
                  </Box>
                </Box>
              </Box>
              
              {/* Content */}
              <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                <Typography 
                  variant="h4" 
                  component="h3"
                  sx={{
                    fontFamily: 'var(--font-family-heading)',
                    fontWeight: 'var(--font-weight-bold)',
                    fontSize: 'var(--font-size-xxl)',
                    color: 'var(--color-text-primary)',
                    mb: 'var(--spacing-sm)'
                  }}
                >
                  {content.patrice_name}
                </Typography>
                
                <Box sx={{ 
                  mb: 'var(--spacing-md)', 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 'var(--spacing-xs)',
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  {(content.patrice_skills ? content.patrice_skills.split('|') : []).map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      size="small"
                      sx={{
                        backgroundColor: index === 0 ? 'var(--color-primary)' : 'transparent',
                        color: index === 0 ? 'var(--color-background-paper)' : 'var(--color-text-secondary)',
                        fontFamily: 'var(--font-family-base)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        border: index === 0 ? 'none' : '1px solid var(--color-action-disabled-background)',
                        borderRadius: '2px',
                        '&:hover': {
                          backgroundColor: index === 0 ? 'var(--color-primary-80)' : 'var(--color-action-hover)',
                        }
                      }}
                    />
                  ))}
                </Box>
                
                <Typography 
                  variant="body1" 
                  component="div"
                  sx={{
                    fontFamily: 'var(--font-family-base)',
                    fontSize: 'var(--font-size-md)',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 'var(--line-height-relaxed)',
                    mb: 'var(--spacing-lg)'
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: content.patrice_bio }} />
                </Typography>
              </Box>
            </Box>
          </MinimalProfileContainer>

          {/* Yves Kim Créac'h */}
          <MinimalProfileContainer>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: 'var(--spacing-xl)',
              alignItems: { xs: 'center', md: 'flex-start' }
            }}>
              {/* Photo and Contact Buttons */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 'var(--spacing-md)',
                flexShrink: 0
              }}>
                <Box sx={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '3px',
                  border: '1px solid var(--color-action-disabled-background)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <Box
                    component="img"
                    src="/team/kim-website-672x448-2b8.jpg"
                    alt="Yves Kim Créac'h"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center 30%',
                      filter: 'grayscale(100%)',
                      transition: 'filter 0.3s ease',
                      '&:hover': {
                        filter: 'grayscale(0%)'
                      }
                    }}
                  />
                </Box>
                
                {/* Contact Buttons */}
                <Box sx={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <Box 
                    component="a"
                    href={`mailto:${content.yves_email}`}
                    sx={{
                      width: '36px',
                      height: '36px',
                      border: '1px solid var(--color-action-disabled-background)',
                      backgroundColor: 'var(--color-background-paper)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      borderRadius: '3px',
                      '&:hover': {
                        borderColor: 'var(--color-primary)',
                        backgroundColor: 'var(--color-primary-5)',
                      }
                    }}
                  >
                    <EmailIcon sx={{ fontSize: '1rem', color: 'var(--color-text-secondary)' }} />
                  </Box>
                  <Box 
                    component="a"
                    href={content.yves_linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: '36px',
                      height: '36px',
                      border: '1px solid var(--color-action-disabled-background)',
                      backgroundColor: 'var(--color-background-paper)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      borderRadius: '3px',
                      '&:hover': {
                        borderColor: 'var(--color-primary)',
                        backgroundColor: 'var(--color-primary-5)',
                      }
                    }}
                  >
                    <LinkedInIcon sx={{ fontSize: '1rem', color: 'var(--color-text-secondary)' }} />
                  </Box>
                </Box>
              </Box>
              
              {/* Content */}
              <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                <Typography 
                  variant="h4" 
                  component="h3"
                  sx={{
                    fontFamily: 'var(--font-family-heading)',
                    fontWeight: 'var(--font-weight-bold)',
                    fontSize: 'var(--font-size-xxl)',
                    color: 'var(--color-text-primary)',
                    mb: 'var(--spacing-sm)'
                  }}
                >
                  {content.yves_name}
                </Typography>
                
                <Box sx={{ 
                  mb: 'var(--spacing-md)', 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 'var(--spacing-xs)',
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  {(content.yves_skills ? content.yves_skills.split('|') : []).map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      size="small"
                      sx={{
                        backgroundColor: index === 0 ? 'var(--color-primary)' : 'transparent',
                        color: index === 0 ? 'var(--color-background-paper)' : 'var(--color-text-secondary)',
                        fontFamily: 'var(--font-family-base)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        border: index === 0 ? 'none' : '1px solid var(--color-action-disabled-background)',
                        borderRadius: '2px',
                        '&:hover': {
                          backgroundColor: index === 0 ? 'var(--color-primary-80)' : 'var(--color-action-hover)',
                        }
                      }}
                    />
                  ))}
                </Box>
                
                <Typography 
                  variant="body1" 
                  component="div"
                  sx={{
                    fontFamily: 'var(--font-family-base)',
                    fontSize: 'var(--font-size-md)',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 'var(--line-height-relaxed)',
                    mb: 'var(--spacing-lg)'
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: content.yves_bio }} />
                </Typography>
              </Box>
            </Box>
          </MinimalProfileContainer>

                     {/* Matthew Lukin Smawfield */}
          <MinimalProfileContainer>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: 'var(--spacing-xl)',
              alignItems: { xs: 'center', md: 'flex-start' }
            }}>
              {/* Photo and Contact Buttons */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 'var(--spacing-md)',
                flexShrink: 0
              }}>
                <Box sx={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '3px',
                  border: '1px solid var(--color-action-disabled-background)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <Box
                    component="img"
                    src="/team/smawfield3.jpeg"
                    alt="Matthew Lukin Smawfield"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center 30%',
                      filter: 'grayscale(100%)',
                      transition: 'filter 0.3s ease',
                      '&:hover': {
                        filter: 'grayscale(0%)'
                      }
                    }}
                  />
                </Box>
                
                {/* Contact Buttons */}
                <Box sx={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <Box 
                    component="a"
                    href={`mailto:${content.matthew_email}`}
                    sx={{
                      width: '36px',
                      height: '36px',
                      border: '1px solid var(--color-action-disabled-background)',
                      backgroundColor: 'var(--color-background-paper)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      borderRadius: '3px',
                      '&:hover': {
                        borderColor: 'var(--color-primary)',
                        backgroundColor: 'var(--color-primary-5)',
                      }
                    }}
                  >
                    <EmailIcon sx={{ fontSize: '1rem', color: 'var(--color-text-secondary)' }} />
                  </Box>
                  {content.matthew_linkedin && (
                    <Box 
                      component="a"
                      href={content.matthew_linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        width: '36px',
                        height: '36px',
                        border: '1px solid var(--color-action-disabled-background)',
                        backgroundColor: 'var(--color-background-paper)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textDecoration: 'none',
                        borderRadius: '3px',
                        '&:hover': {
                          borderColor: 'var(--color-primary)',
                          backgroundColor: 'var(--color-primary-5)',
                        }
                      }}
                    >
                      <LinkedInIcon sx={{ fontSize: '1rem', color: 'var(--color-text-secondary)' }} />
                    </Box>
                  )}
                </Box>
              </Box>
              
              {/* Content */}
              <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                <Typography 
                  variant="h4" 
                  component="h3"
                  sx={{
                    fontFamily: 'var(--font-family-heading)',
                    fontWeight: 'var(--font-weight-bold)',
                    fontSize: 'var(--font-size-xxl)',
                    color: 'var(--color-text-primary)',
                    mb: 'var(--spacing-sm)'
                  }}
                >
                  {content.matthew_name}
                </Typography>
                
                <Box sx={{ 
                  mb: 'var(--spacing-md)', 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 'var(--spacing-xs)',
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  {(content.matthew_skills ? content.matthew_skills.split('|') : []).map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      size="small"
                      sx={{
                        backgroundColor: index === 0 ? 'var(--color-primary)' : 'transparent',
                        color: index === 0 ? 'var(--color-background-paper)' : 'var(--color-text-secondary)',
                        fontFamily: 'var(--font-family-base)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        border: index === 0 ? 'none' : '1px solid var(--color-action-disabled-background)',
                        borderRadius: '2px',
                        '&:hover': {
                          backgroundColor: index === 0 ? 'var(--color-primary-80)' : 'var(--color-action-hover)',
                        }
                      }}
                    />
                  ))}
                </Box>
                
                <Typography 
                  variant="body1" 
                  component="div"
                  sx={{
                    fontFamily: 'var(--font-family-base)',
                    fontSize: 'var(--font-size-md)',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 'var(--line-height-relaxed)',
                    mb: 'var(--spacing-lg)'
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: content.matthew_bio }} />
                </Typography>
              </Box>
            </Box>
          </MinimalProfileContainer>
        </Box>
      </FeatureSection>

      {/* Contact Information */}
      <ContentSection alternate sx={{ mb: 0, pb: 'clamp(2.5rem, 5vw, 4rem)' }} id="contact-section">
        <Container maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontFamily: 'var(--font-family-heading)',
              fontWeight: 'var(--font-weight-bold)',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              color: 'var(--color-text-primary)',
              mb: 'var(--spacing-md)',
              lineHeight: 'var(--line-height-tight)',
              letterSpacing: 'var(--letter-spacing-tight)'
            }}
          >
            {content.contact_title}
          </Typography>
          <Typography 
            variant="body1" 
            component="div" 
            align="center" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-lg)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--line-height-relaxed)',
              mb: 'var(--spacing-xl)', 
              maxWidth: '800px', 
              mx: 'auto',
              fontWeight: 'var(--font-weight-normal)',
              '& a': {
                color: 'var(--color-primary)',
                textDecoration: 'none',
                fontWeight: 'var(--font-weight-medium)',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: content.contact_description }} />
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
            gap: 'var(--spacing-lg)',
            maxWidth: '800px',
            mx: 'auto'
          }}>
            <CleanContactBlock
              component="a"
              href={`mailto:${content.contact_email}`}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                cursor: 'pointer'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                <Box sx={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'var(--color-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <EmailIcon sx={{ fontSize: '1.5rem', color: 'var(--color-background-paper)' }} />
                </Box>
                <Box>
                  <Typography 
                    variant="h4" 
                    sx={{
                      fontFamily: 'var(--font-family-heading)',
                      fontWeight: 'var(--font-weight-bold)',
                      fontSize: 'var(--font-size-xl)',
                      color: 'var(--color-text-primary)',
                      mb: 'var(--spacing-xs)'
                    }}
                  >
                    {content.contact_email_label}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{
                      fontFamily: 'var(--font-family-base)',
                      fontSize: 'var(--font-size-lg)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 'var(--font-weight-medium)'
                    }}
                  >
                    {content.contact_email}
                  </Typography>
                </Box>
              </Box>
            </CleanContactBlock>

            <CleanContactBlock>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                <Box sx={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'var(--color-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <LocationOnIcon sx={{ fontSize: '1.5rem', color: 'var(--color-background-paper)' }} />
                </Box>
                <Box>
                  <Typography 
                    variant="h4" 
                    sx={{
                      fontFamily: 'var(--font-family-heading)',
                      fontWeight: 'var(--font-weight-bold)',
                      fontSize: 'var(--font-size-xl)',
                      color: 'var(--color-text-primary)',
                      mb: 'var(--spacing-xs)'
                    }}
                  >
                    {content.contact_address_label}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    component="div"
                    sx={{
                      fontFamily: 'var(--font-family-base)',
                      fontSize: 'var(--font-size-lg)',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 'var(--line-height-normal)',
                      fontWeight: 'var(--font-weight-medium)'
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: content.contact_address.replace(/\n/g, '<br />') }} />
                  </Typography>
                </Box>
              </Box>
            </CleanContactBlock>
          </Box>
        </Container>
      </ContentSection>

      {/* Footer */}
      <Box sx={{ 
        bgcolor: '#005F94', // Force blue color for landing page
        color: 'var(--color-background-paper)', 
        py: 'var(--spacing-lg)', 
        mt: 0,
        borderTop: '1px solid var(--color-action-disabled-background)'
      }}>
        <Container maxWidth={false} sx={{ maxWidth: '1320px', mx: 'auto', px: 3 }}>
          <Typography 
            variant="body2" 
            align="center"
            sx={{
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-md)',
              color: 'var(--color-background-paper)',
              lineHeight: 'var(--line-height-normal)',
              fontWeight: 'var(--font-weight-normal)',
              opacity: 0.9
            }}
          >
            © 2025 Situation Room. All rights reserved.
          </Typography>
        </Container>
      </Box>

      {/* Framework Modal */}
      <Dialog
        open={frameworkModalOpen}
        onClose={() => setFrameworkModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'var(--color-background-paper)',
            borderRadius: '12px',
            boxShadow: '0 24px 48px rgba(0, 95, 148, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'var(--font-family-heading)',
          fontWeight: 'var(--font-weight-bold)',
          fontSize: 'var(--font-size-xl)',
          color: 'var(--color-text-primary)',
          pb: 'var(--spacing-sm)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {content.framework_modal_title}
          <IconButton 
            onClick={() => setFrameworkModalOpen(false)}
            sx={{ 
              color: 'var(--color-text-secondary)',
              '&:hover': {
                backgroundColor: 'var(--color-action-hover)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 'var(--spacing-lg)' }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Box
              component="img"
              src="/framework.svg"
              alt="Detailed Analytical Framework Diagram"
              sx={{
                width: '100%',
                height: 'auto',
                maxWidth: '800px',
                borderRadius: '12px',
                objectFit: 'contain',
                backgroundColor: 'transparent',
                boxShadow: '0 4px 20px rgba(0, 95, 148, 0.1)'
              }}
            />
          </Box>
          <Typography 
            variant="caption" 
            align="center" 
            sx={{ 
              fontFamily: 'var(--font-family-base)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              fontStyle: 'italic',
              mt: 'var(--spacing-md)',
              display: 'block',
              lineHeight: 'var(--line-height-relaxed)'
            }}
          >
            The framework has been tested and refined over a ten years period and was used in multiple crises by multiple NGOs or initiatives, e.g, DFS, NRC, SI, PAH, OP, OA, iMMAP Inc., DEEP, IFRC, etc.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LandingPage; 